// Fictional demonstration data for the landing-page product visuals.
//
// SAFE DATA: everything here is invented for the marketing page. No real
// customer/employee data, no real email addresses (only reserved .example
// TLDs where an address is unavoidable), no secrets, no live DB reads. One
// coherent fictional company — "Riverside Field Services", a commercial
// HVAC & mechanical contractor — so every screen tells the same story.
//
// R3: data density + real status metadata raised to mirror the actual product
// screens (Field Hub, Inbox, Deals, Analytics, Automation Health, Fleet).

export const COMPANY = {
  name: 'Riverside Field Services',
  trade: 'Commercial HVAC & Mechanical',
  tier: 'Enterprise',
}

// ─── People ───────────────────────────────────────────────────────────────
export interface Person { id: string; name: string; initials: string; role: string; grad: string }

export const TEAM: Person[] = [
  { id: 'u-ray',    name: 'Ray Delgado',    initials: 'RD', role: 'Owner',        grad: 'var(--identity-gradient-1)' },
  { id: 'u-maya',   name: 'Maya Chen',      initials: 'MC', role: 'Dispatch lead', grad: 'var(--identity-gradient-2)' },
  { id: 'u-diego',  name: 'Diego Ramirez',  initials: 'DR', role: 'Lead tech',     grad: 'var(--identity-gradient-3)' },
  { id: 'u-priya',  name: 'Priya Patel',    initials: 'PP', role: 'Technician',    grad: 'var(--identity-gradient-5)' },
  { id: 'u-marcus', name: 'Marcus Bell',    initials: 'MB', role: 'Technician',    grad: 'var(--identity-gradient-4)' },
  { id: 'u-tess',   name: 'Tess Okafor',    initials: 'TO', role: 'Technician',    grad: 'var(--identity-gradient-2)' },
  { id: 'u-jordan', name: 'Jordan Hale',    initials: 'JH', role: 'Office admin',  grad: 'var(--identity-gradient-3)' },
]
export const byId = (id: string) => TEAM.find(p => p.id === id)!

// ─── Dispatch board (Field Hub) ─────────────────────────────────────────────
export type JobStatus =
  | 'scheduled' | 'en_route' | 'on_site' | 'complete'
  | 'pending_parts' | 'awaiting_approval'

export interface DemoJob {
  id: string; time: string; customer: string; site: string; service: string
  tech: string; status: JobStatus; priority?: 'normal' | 'high' | 'urgent'; conflict?: boolean
}

export const JOBS: DemoJob[] = [
  { id: 'j1', time: '8:00',  customer: 'Northgate Apartments', site: '3 rooftop units',   service: 'Quarterly PM',        tech: 'u-diego',  status: 'on_site',           priority: 'normal' },
  { id: 'j2', time: '8:30',  customer: 'Harborview Dental',    site: 'Suite 200',         service: 'No cooling — call',   tech: 'u-priya',  status: 'en_route',          priority: 'urgent' },
  { id: 'j3', time: '9:15',  customer: 'Lakeside Grill',       site: 'Walk-in cooler',    service: 'Compressor swap',     tech: 'u-marcus', status: 'awaiting_approval',  priority: 'high' },
  { id: 'j4', time: '10:00', customer: 'Cedar Ridge HOA',      site: 'Clubhouse RTU-2',   service: 'Belt & filter',       tech: 'u-tess',   status: 'scheduled',         priority: 'normal' },
  { id: 'j5', time: '11:30', customer: 'Meridian Office Park', site: 'Bldg C mechanical', service: 'Thermostat retrofit', tech: 'u-diego',  status: 'scheduled',         priority: 'normal', conflict: true },
  { id: 'j6', time: '1:00',  customer: 'Sunrise Senior Living',site: 'Boiler room',       service: 'Pump inspection',     tech: 'u-priya',  status: 'pending_parts',     priority: 'high' },
  { id: 'j7', time: '2:30',  customer: 'Lakeside Grill',       site: 'Line 2 makeup air', service: 'Warranty follow-up',  tech: 'u-marcus', status: 'scheduled',         priority: 'normal' },
  { id: 'j8', time: '3:45',  customer: 'Northgate Apartments', site: 'Bldg 4 split',      service: 'Refrigerant leak',    tech: 'u-tess',   status: 'scheduled',         priority: 'high' },
]

// Real Field Hub stat row + filters/legend (mirrors FieldHub.tsx chrome).
export const FIELD_STATS = [
  { label: 'This period', value: '63' },
  { label: 'Active now',  value: '6',  tone: 'var(--accent-text)' },
  { label: 'Scheduled',   value: '25' },
  { label: 'On hold',     value: '5' },
  { label: 'Complete',    value: '23' },
]
export const FIELD_VIEWS = ['Calendar', 'Job board', 'Live map', 'Crew']
export const FIELD_FILTERS = ['All', 'Scheduled', 'En route', 'On site', 'On hold', 'Complete', 'Unassigned']

export const JOB_STATUS_META: Record<JobStatus, { label: string; token: string }> = {
  scheduled:         { label: 'Scheduled',     token: 'var(--status-job-scheduled)' },
  en_route:          { label: 'En route',      token: 'var(--status-job-en-route)' },
  on_site:           { label: 'On site',       token: 'var(--status-job-on-site)' },
  complete:          { label: 'Complete',      token: 'var(--positive)' },
  pending_parts:     { label: 'Pending parts', token: 'var(--status-job-pending-parts)' },
  awaiting_approval: { label: 'Needs approval', token: 'var(--status-job-awaiting-approval)' },
}

// ─── Invoice (InvoiceDetail) ────────────────────────────────────────────────
export const INVOICE = {
  number: 'INV-1042',
  customer: 'Lakeside Grill',
  contact: 'Dana Kim',
  status: 'Sent',
  terms: 'Net 15',
  due: 'Jul 14',
  lines: [
    { desc: 'Walk-in cooler compressor (R-448A)', qty: 1, unit: 1480 },
    { desc: 'Labor — compressor swap & evac',     qty: 4, unit: 145 },
    { desc: 'Filter drier + line set',            qty: 1, unit: 96 },
  ],
  taxRate: 0.08,
  marginPct: 41,
}
// Invoices-list stat row (mirrors Invoices.tsx metric cards).
export const INVOICE_STATS = [
  { label: 'Outstanding', value: '$38,410', tone: 'var(--accent-text)' },
  { label: 'Overdue',     value: '$4,180',  tone: 'var(--danger)' },
  { label: 'Collected',   value: '$212,900', tone: 'var(--positive)' },
  { label: 'Invoices',    value: '146',     tone: 'var(--info)' },
]

// ─── Shared inbox ───────────────────────────────────────────────────────────
export interface Conversation {
  id: string; channel: 'email' | 'phone' | 'portal' | 'chat'; from: string; subject: string
  preview: string; when: string; priority: 'urgent' | 'high' | 'normal' | 'low'
  assignee?: string; unread?: number; sla?: 'breached' | string
}
export const CONVERSATIONS: Conversation[] = [
  { id: 'c1', channel: 'email',  from: 'Harborview Dental',    subject: 'No cooling in Suite 200',      preview: 'It got warm overnight — patients arrive at 9…', when: '7m',  priority: 'urgent', assignee: 'u-maya',   unread: 3, sla: '38m' },
  { id: 'c2', channel: 'portal', from: 'Cedar Ridge HOA',      subject: 'Clubhouse PM scheduling',       preview: 'Requested through the client portal',           when: '22m', priority: 'normal', assignee: 'u-jordan' },
  { id: 'c3', channel: 'phone',  from: 'Lakeside Grill',       subject: 'Call — walk-in cooler down',    preview: 'Logged by Maya · needs approval on quote',       when: '1h',  priority: 'high',   assignee: 'u-maya' },
  { id: 'c4', channel: 'email',  from: 'Jennifer Park',        subject: 'Annual HVAC plan — questions',  preview: 'Got the quote — a couple of questions before…',  when: '3h',  priority: 'normal', sla: 'breached', unread: 1 },
  { id: 'c5', channel: 'chat',   from: 'Marcus Bell',          subject: 'Onboarding support request',    preview: 'New tech needs portal access set up',            when: '4h',  priority: 'low',    assignee: 'u-jordan' },
  { id: 'c6', channel: 'email',  from: 'Meridian Office Park', subject: 'Re: Thermostat retrofit quote', preview: 'Approved — go ahead and schedule.',              when: '5h',  priority: 'normal', assignee: 'u-jordan' },
]

// ─── Team chat ──────────────────────────────────────────────────────────────
export const CHAT_CHANNEL = '#dispatch'
export interface ChatMsg { id: string; who: string; text: string; when: string; reactions?: { emoji: string; n: number }[]; thread?: number }
export const CHAT: ChatMsg[] = [
  { id: 'm1', who: 'u-maya',   text: 'Harborview is a no-cooling emergency — moving Priya there now, pushing Cedar Ridge to 10.', when: '9:02', reactions: [{ emoji: '👍', n: 3 }] },
  { id: 'm2', who: 'u-priya',  text: 'On it. En route, ~15 min out.', when: '9:03' },
  { id: 'm3', who: 'u-marcus', text: 'Lakeside compressor is toast. Quote sent for approval — need a yes before I pull it.', when: '9:05', thread: 2 },
  { id: 'm4', who: 'u-ray',    text: 'Approved. Bill it Net 15.', when: '9:06', reactions: [{ emoji: '✅', n: 2 }] },
  { id: 'm5', who: 'u-jordan', text: 'Invoice INV-1042 is out. Cedar Ridge rescheduled to 10:00.', when: '9:08' },
]

// ─── Orkestra Meetings ──────────────────────────────────────────────────────
export const MEETING = {
  title: 'Cedar Ridge — annual service review',
  participants: [
    { id: 'u-ray',  label: 'Ray Delgado (you)' },
    { id: 'g1',     label: 'Dana — Cedar Ridge', guest: true },
    { id: 'u-maya', label: 'Maya Chen' },
    { id: 'u-jordan', label: 'Jordan Hale' },
  ],
  elapsed: '14:22',
  chat: [
    { who: 'Maya Chen', text: 'Sending the PM schedule now.', when: '14:03' },
    { who: 'Dana (guest)', text: 'Perfect — board will want the RTU line items.', when: '14:05' },
  ],
}

// ─── Fleet ──────────────────────────────────────────────────────────────────
export interface Vehicle {
  id: string; name: string; make: string; plate: string; odo: string
  status: 'active' | 'in_shop' | 'retired'; assigned?: string
  due: 'ok' | 'due_soon' | 'overdue'; dueLabel: string
}
export const FLEET: Vehicle[] = [
  { id: 'v1', name: 'Van 04',   make: 'Ford Transit 250 · 2022', plate: 'RFS-204', odo: '61,240', status: 'active',  assigned: 'u-diego',  due: 'due_soon', dueLabel: 'Oil service · 320 mi' },
  { id: 'v2', name: 'Truck 12', make: 'RAM 2500 · 2021',         plate: 'RFS-112', odo: '88,905', status: 'in_shop', assigned: 'u-marcus', due: 'overdue',  dueLabel: 'Brake inspection' },
  { id: 'v3', name: 'Van 07',   make: 'Ford Transit 350 · 2023', plate: 'RFS-207', odo: '34,110', status: 'active',  assigned: 'u-priya',  due: 'ok',       dueLabel: '—' },
  { id: 'v4', name: 'Van 02',   make: 'Chevy Express · 2020',    plate: 'RFS-202', odo: '104,700',status: 'active',  assigned: 'u-tess',   due: 'due_soon', dueLabel: 'Tire rotation · 11 days' },
  { id: 'v5', name: 'Truck 09', make: 'Ford F-250 · 2019',       plate: 'RFS-109', odo: '132,540',status: 'retired', assigned: undefined,  due: 'ok',       dueLabel: '—' },
]
export const FLEET_STATS = [
  { label: 'Vehicles', value: '9' },
  { label: 'Due soon', value: '2', tone: 'var(--warning)' },
  { label: 'Overdue',  value: '1', tone: 'var(--danger)' },
]

// ─── Analytics ──────────────────────────────────────────────────────────────
// 12-point revenue-collected series (thousands) — plausible seasonal ramp.
export const REVENUE_SERIES = [34, 38, 41, 39, 46, 52, 49, 58, 63, 61, 72, 78]
// 6 real Analytics overview cards (mirrors Analytics.tsx).
export const ANALYTICS_STATS = [
  { label: 'Revenue collected', value: '$228,400', delta: '+18%', tone: 'var(--positive)' },
  { label: 'Jobs completed',    value: '184',      delta: '+12%', tone: 'var(--warning)' },
  { label: 'Deals won',         value: '31',       delta: '+6',   tone: 'var(--info)' },
  { label: 'Avg. margin',       value: '41.2%',    delta: '+3.4pt', tone: 'var(--accent-text)' },
  { label: 'Quote conversion',  value: '68%',      delta: '+4pt', tone: 'var(--accent-text)' },
  { label: 'Avg. review',       value: '4.9★',     delta: '+0.1', tone: 'var(--warning)' },
]
// Quote pipeline breakdown bars (mirrors Analytics status-breakdown section).
export const PIPELINE = [
  { stage: 'Draft',    value: 40, count: 8 },
  { stage: 'Sent',     value: 71, count: 14 },
  { stage: 'Approved', value: 52, count: 9 },
  { stage: 'Invoiced', value: 33, count: 6 },
]

// ─── Automation Health (real chain names) ───────────────────────────────────
export const HEARTBEAT = { label: 'Healthy', runs24: 42, failed24: 0, last: '41s ago' }
export const SWEEPS = [
  { name: 'Overdue check',                 queued: 3,  next: 'in 3h 12m', ok: true },
  { name: 'Payment reminders',             queued: 11, next: 'in 3h 12m', ok: true },
  { name: 'Recurring invoice generation',  queued: 6,  next: 'in 8h 40m', ok: true },
]
export const PROCESSOR_RUNS = [
  { ran: '3h ago', dur: '820ms', processed: 11, sent: 11, failed: 0 },
  { ran: '9h ago', dur: '1.2s',  processed: 6,  sent: 6,  failed: 0 },
  { ran: '15h ago', dur: '640ms', processed: 4, sent: 4,  failed: 0 },
]

// ─── Requests (RequestsInbox) — used by capability visuals if needed ─────────
export const REQUEST_STATUS_META: Record<string, { label: string; token: string }> = {
  new:         { label: 'New',         token: 'var(--info)' },
  in_review:   { label: 'In review',   token: 'var(--accent-text)' },
  in_progress: { label: 'In progress', token: 'var(--warning)' },
  converted:   { label: 'Converted',   token: 'var(--positive)' },
  declined:    { label: 'Declined',    token: 'var(--danger)' },
}

// ─── Tools Orkestra consolidates (for the "replaces" section) ────────────────
export const REPLACES = [
  'CRM', 'Field dispatch', 'Quoting', 'Invoicing', 'Shared inbox',
  'Team chat', 'Video calls', 'Fleet tracking', 'Reporting', 'Spreadsheets',
]

// ─── Nav rail (mirrors the real app sidebar order) ──────────────────────────
export const NAV_ITEMS = ['dashboard', 'comms', 'field', 'sales', 'fleet', 'reporting', 'ai', 'settings'] as const
